import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const TabContext = createContext(null);

// Tab sistemi kullanabilecek roller
const TAB_ENABLED_ROLES = ['superadmin', 'firma', 'admin', 'mudur', 'yetsanadmin', 'ithaladmin'];

// Route -> Tab label mapping
const ROUTE_LABELS = {
  '/panel/anasayfa': 'Ana Sayfa',
  '/panel/urunler': 'Urun Yonetimi',
  '/panel/yeni-siparis': 'Yeni Siparis',
  '/panel/siparisler': 'Siparisler',
  '/panel/siparis-izni-talepleri': 'Sipariş İzinleri',
  '/panel/teklifler': 'Teklifler',
  '/panel/teklif-listesi': 'Teklif Listesi',
  '/panel/hizli-teklif-olustur': 'Hizli Teklif',
  '/panel/cari': 'Cari Hesap',
  '/panel/cari-hesaplar': 'Cari Hesaplar',
  '/panel/musteriler': 'Musteriler',
  '/panel/panel-musterileri': 'Panel Musterileri',
  '/panel/ithalat': 'Ithalat',
  '/panel/depo-transfer': 'Depo Transfer',
  '/panel/stok-resync': 'Stok Eşitleme',
  '/panel/yoldaki-urunlerim': 'Yoldaki Urunler',
  '/panel/talep-radari': 'Talep Radari',
  '/panel/planlama': 'Planlama',
  '/panel/kasa': 'Kasa',
  '/panel/tecdoc': 'Parça Arama (TecDoc)',
  '/panel/on-siparislerim': 'On Siparisler',
  '/panel/on-siparis-firsatlari': 'Ön Sipariş Fırsatları',
  '/panel/on-satis-yonetim': 'Ön-Satış Yönetimi',
  '/panel/sepetim': 'Sepetim',
  '/panel/yasal-metinler': 'Yasal Metinler',
  '/panel/raporlar': 'Raporlar',
  '/panel/fiyat-talepleri': 'Fiyat Talepleri',
  '/panel/taleplerim': 'Taleplerim',
  '/panel/iskonto-onay': 'Iskonto Onay',
  '/panel/iskonto-tanimlari': 'Iskonto Tanimlari',
  '/panel/yeni-urun': 'Yeni Urun',
  '/panel/promosyon-merkezi': 'Promosyon',
  '/panel/kullanici-yonetimi': 'Kullanici Yonetimi',
  '/panel/fiyat-yonetimi': 'Fiyat Yonetimi',
  '/panel/sistem-yonetimi': 'Sistem Yonetimi',
  '/panel/firma-dashboard': 'Firma Dashboard',
  '/panel/kredi-karti-provizyon': 'Kredi Karti',
  '/panel/iade': 'Satış İadesi',
  '/panel/bayi-basvurulari': 'Bayi Başvuruları',
  '/panel/arac-satis': 'Araçtan Satış',
  '/panel/sistem-bildirimleri': 'Bildirimler',
  '/panel/musteri-cari': 'Cari Hesabim',
  '/panel/uyelik': 'Uyelik',
  '/panel/adreslerim': 'Adreslerim',
  '/panel/sifre-degistir': 'Sifre Degistir',
  '/panel/siparislerim': 'Siparislerim',
  '/panel/canli-analitik': 'Canli Analitik',
  '/panel/cip': 'CIP',
  '/panel/musteri-gecmisi': 'Musteri Gecmisi',
  '/panel/cohort-analysis': 'Cohort Analiz',
  '/panel/kullanici-aktiviteleri': 'Kullanici Aktiviteleri',
  '/yonetici-dashboard': 'Yonetici Dashboard',
  '/wms/dashboard': 'WMS Dashboard',
  '/wms/ana-menu': 'WMS Menu',
  '/wms/siparisler': 'WMS Siparisler',
  '/wms/raf-yonetimi': 'Raf Yonetimi',
  '/wms/urun-raf': 'Urun Raf',
  '/wms/giris-fisleri': 'Giris Fisleri',
  '/wms/cikis-fisleri': 'Cikis Fisleri',
  '/wms/envanter-sayim': 'Depo Sayımı',
  '/wms/barkod-okuyucu': 'Barkod Okuyucu',
  '/wms/hizli-islemler': 'Hizli Islemler',
  '/wms/siparis-monitor': 'Siparis Monitor',
  '/wms/ithalat-giris': 'Ithalat Giris',
  '/wms/etiket-tasarim': 'Etiket Tasarim',
  '/wms/ayarlar': 'WMS Ayarlar',
  '/wms/sistem-loglari': 'Sistem Loglari',
};

function getTabLabel(path) {
  if (ROUTE_LABELS[path]) return ROUTE_LABELS[path];
  if (path.startsWith('/panel/musteri-detay/')) return 'Musteri Detay';
  if (path.startsWith('/panel/siparisler/')) return 'Siparisler';
  if (path.startsWith('/wms/siparis/')) return 'WMS Siparis';
  if (path.startsWith('/wms/cikis-fisi/')) return 'Cikis Fisi';
  if (path.startsWith('/wms/giris-fisi/')) return 'Giris Fisi';
  if (path.startsWith('/wms/ithalat-giris/')) return 'Ithalat Giris';
  const last = path.split('/').pop();
  return last ? last.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Sayfa';
}

// localStorage helpers - kullanıcı bazlı
function getPinnedKey(userId) { return `yetsan_pinned_tabs_${userId}`; }

function loadPinnedTabs(userId) {
  try {
    const raw = localStorage.getItem(getPinnedKey(userId));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function savePinnedTabs(userId, paths) {
  try { localStorage.setItem(getPinnedKey(userId), JSON.stringify(paths)); } catch {}
}

export function TabProvider({ children }) {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [pinnedPaths, setPinnedPaths] = useState([]);
  const [pinnedLoaded, setPinnedLoaded] = useState(false);
  const [closedPaths, setClosedPaths] = useState(new Set()); // Son kapatılan tab path'leri
  const [isFullscreen, setIsFullscreen] = useState(() => {
    try { return localStorage.getItem('yetsan_fullscreen') === 'true'; } catch { return false; }
  });

  const isTabEnabled = useCallback((role) => {
    return TAB_ENABLED_ROLES.includes(role);
  }, []);

  // Sabitleri yükle (login sonrası çağrılır)
  const loadPinnedForUser = useCallback((userId) => {
    if (!userId) return;
    let pins = loadPinnedTabs(userId);
    // 🔧 CRM tabları artık tab sistemine dahil değil — eski kaydolmuş CRM pin'leri temizle
    const filtered = pins.filter(p => !p.startsWith('/crm'));
    if (filtered.length !== pins.length) {
      pins = filtered;
      savePinnedTabs(userId, pins);
    }
    setPinnedPaths(pins);
    setPinnedLoaded(true);
    // Sabitlenmiş tab'ları aç
    if (pins.length > 0) {
      setTabs(prev => {
        let newTabs = [...prev];
        pins.forEach(path => {
          if (!newTabs.find(t => t.path === path)) {
            newTabs.push({ id: `tab-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, path, label: getTabLabel(path), refreshKey: 0, pinned: true });
          } else {
            newTabs = newTabs.map(t => t.path === path ? { ...t, pinned: true } : t);
          }
        });
        // İlk sabitlenmiş tab'ı aktif yap
        if (!newTabs.find(t => t.id === activeTabId)) {
          const firstPin = newTabs.find(t => t.pinned);
          if (firstPin) setActiveTabId(firstPin.id);
        }
        return newTabs;
      });
    }
  }, [activeTabId]);

  // Tab sabitle / sabitlenmesini kaldır
  const togglePinTab = useCallback((tabId, userId) => {
    setTabs(prev => {
      const tab = prev.find(t => t.id === tabId);
      if (!tab) return prev;
      const newPinned = !tab.pinned;
      const updated = prev.map(t => t.id === tabId ? { ...t, pinned: newPinned } : t);
      // localStorage güncelle
      const newPins = updated.filter(t => t.pinned).map(t => t.path);
      setPinnedPaths(newPins);
      if (userId) savePinnedTabs(userId, newPins);
      return updated;
    });
  }, []);

  // Tab aç veya mevcut tab'a geç
  const openTab = useCallback((path) => {
    // Son kapatılan path ise açma (URL tekrar tetiklemesin)
    if (closedPaths.has(path)) return;
    setTabs(prev => {
      const existing = prev.find(t => t.path === path);
      if (existing) {
        setActiveTabId(existing.id);
        return prev;
      }
      const isPinned = pinnedPaths.includes(path);
      const newTab = { id: `tab-${Date.now()}`, path, label: getTabLabel(path), refreshKey: 0, pinned: isPinned };
      setActiveTabId(newTab.id);
      return [...prev, newTab];
    });
  }, [pinnedPaths, closedPaths]);

  // Tab aç - sidebar/kullanıcı tıklaması ile (closedPaths bypass)
  const forceOpenTab = useCallback((path) => {
    setClosedPaths(cp => { const n = new Set(cp); n.delete(path); return n; });
    setTabs(prev => {
      const existing = prev.find(t => t.path === path);
      if (existing) {
        setActiveTabId(existing.id);
        return prev;
      }
      const isPinned = pinnedPaths.includes(path);
      const newTab = { id: `tab-${Date.now()}`, path, label: getTabLabel(path), refreshKey: 0, pinned: isPinned };
      setActiveTabId(newTab.id);
      return [...prev, newTab];
    });
  }, [pinnedPaths]);

  // Tab refresh
  const refreshTab = useCallback((tabId) => {
    setTabs(prev => prev.map(t =>
      t.id === tabId ? { ...t, refreshKey: (t.refreshKey || 0) + 1 } : t
    ));
  }, []);

  // Path ile refresh (browser back/forward → aktif tab remount için)
  const refreshTabByPath = useCallback((path) => {
    setTabs(prev => prev.map(t =>
      t.path === path ? { ...t, refreshKey: (t.refreshKey || 0) + 1 } : t
    ));
  }, []);

  // Tab kapat (sabitlenmiş tab'lar kapatılamaz)
  const closeTab = useCallback((tabId) => {
    setTabs(prev => {
      const tab = prev.find(t => t.id === tabId);
      if (!tab) return prev;
      if (tab.pinned) return prev;
      // Kapatılan path'i blacklist'e ekle (URL tekrar açmasın)
      setClosedPaths(cp => new Set([...cp, tab.path]));
      const idx = prev.findIndex(t => t.id === tabId);
      const newTabs = prev.filter(t => t.id !== tabId);
      if (activeTabId === tabId && newTabs.length > 0) {
        const newIdx = Math.min(idx, newTabs.length - 1);
        setActiveTabId(newTabs[newIdx].id);
      }
      if (newTabs.length === 0) setActiveTabId(null);
      return newTabs;
    });
  }, [activeTabId]);

  const switchTab = useCallback((tabId) => {
    setActiveTabId(tabId);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
      try { localStorage.setItem('yetsan_fullscreen', 'true'); } catch {}
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
      try { localStorage.setItem('yetsan_fullscreen', 'false'); } catch {}
    }
  }, []);

  const applyFullscreenPreference = useCallback(() => {
    try {
      const pref = localStorage.getItem('yetsan_fullscreen');
      if (pref === 'true' && !document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
        setIsFullscreen(true);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const handler = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      try { localStorage.setItem('yetsan_fullscreen', fs ? 'true' : 'false'); } catch {}
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const getActiveTab = useCallback(() => {
    return tabs.find(t => t.id === activeTabId) || null;
  }, [tabs, activeTabId]);

  return (
    <TabContext.Provider value={{
      tabs, activeTabId, isFullscreen, pinnedPaths, pinnedLoaded,
      openTab, forceOpenTab, closeTab, switchTab, refreshTab, refreshTabByPath, toggleFullscreen, togglePinTab,
      isTabEnabled, getActiveTab, applyFullscreenPreference, loadPinnedForUser,
    }}>
      {children}
    </TabContext.Provider>
  );
}

export function useTabSystem() {
  const ctx = useContext(TabContext);
  if (!ctx) throw new Error('useTabSystem must be used within TabProvider');
  return ctx;
}

export { TAB_ENABLED_ROLES, ROUTE_LABELS, getTabLabel };
